// Licensed to the Apache Software Foundation (ASF) under one
// or more contributor license agreements.  See the NOTICE file
// distributed with this work for additional information
// regarding copyright ownership.  The ASF licenses this file
// to you under the Apache License, Version 2.0 (the
// "License"); you may not use this file except in compliance
// with the License.  You may obtain a copy of the License at
//
//   http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing,
// software distributed under the License is distributed on an
// "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
// KIND, either express or implied.  See the License for the
// specific language governing permissions and limitations
// under the License.

<template>
  <div class="user-layout-login">
      <a-form
        id="formLogin"
        class="form"
        :ref="formRef"
        :model="form"
        :rules="rules"
        @finish="handleSubmit"
        v-ctrl-enter="handleSubmit"
      >
      <h1 style="margin-bottom: 34px;">Login</h1>
        <a-tabs
          class="tab-center"
          :activeKey="customActiveKey"
          size="large"
          :tabBarStyle="{ textAlign: 'center', borderBottom: 'unset' }"
          @change="handleTabClick"
          :animated="false"
        >
          <a-tab-pane key="cs">
            <template #tab>
              <span>
                <safety-outlined />
                {{ $t('label.login.portal') }}
              </span>
            </template>
            <a-form-item v-if="$config.multipleServer" name="server" ref="server">
              <a-select
                size="large"
                :placeholder="$t('server')"
                v-model:value="form.server"
                @change="onChangeServer"
                showSearch
                optionFilterProp="label"
                :filterOption="(input, option) => {
                  return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }">
                <a-select-option v-for="item in $config.servers" :key="(item.apiHost || '') + item.apiBase" :label="item.name">
                  <template #prefix>
                    <database-outlined />
                  </template>
                  {{ item.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
            <a-form-item ref="username" name="username">
              <a-input
                size="large"
                type="text"
                v-focus="true"
                :placeholder="$t('label.username')"
                v-model:value="form.username"
              >
                <template #prefix>
                  <user-outlined />
                </template>
              </a-input>
            </a-form-item>
            <a-form-item ref="password" name="password">
              <a-input-password
                size="large"
                type="password"
                autocomplete="false"
                :placeholder="$t('label.password')"
                v-model:value="form.password"
              >
                <template #prefix>
                  <lock-outlined />
                </template>
              </a-input-password>
            </a-form-item>
            <a-form-item ref="domain" name="domain">
              <a-input
                size="large"
                type="text"
                :placeholder="$t('label.domain')"
                v-model:value="form.domain"
              >
                <template #prefix>
                  <block-outlined />
                </template>
              </a-input>
            </a-form-item>
          </a-tab-pane>
          <a-tab-pane key="saml" :disabled="idps.length === 0">
            <template #tab>
              <span>
                <audit-outlined />
                {{ $t('label.login.single.signon') }}
              </span>
            </template>
            <a-form-item v-if="$config.multipleServer" name="server" ref="server">
              <a-select
                size="large"
                :placeholder="$t('server')"
                v-model:value="form.server"
                @change="onChangeServer"
                showSearch
                optionFilterProp="label"
                :filterOption="(input, option) => {
                  return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }" >
                <a-select-option v-for="item in $config.servers" :key="(item.apiHost || '') + item.apiBase" :label="item.name">
                  <template #prefix>
                    <database-outlined />
                  </template>
                  {{ item.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
            <a-form-item name="idp" ref="idp">
              <a-select
                v-model:value="form.idp"
                showSearch
                optionFilterProp="label"
                :filterOption="(input, option) => {
                  return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }" >
                <a-select-option v-for="(idp, idx) in idps" :key="idx" :value="idp.id" :label="idp.orgName">
                  {{ idp.orgName }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-tab-pane>
        </a-tabs>
        <a-form-item>
          <a-button
            size="large"
            type="primary"
            html-type="submit"
            class="login-button"
            :loading="state.loginBtn"
            :disabled="state.loginBtn"
            ref="submit"
            @click="handleSubmit"
          >{{ $t('label.login') }}</a-button>
        </a-form-item>
        <div>
        </div>
        <a-row justify="space-between">
          <a-col>
          <translation-menu/>
          </a-col>
          <a-col v-if="forgotPasswordEnabled">
            <router-link :to="{ name: 'forgotPassword' }">
              {{ $t('label.forgot.password') }}
            </router-link>
          </a-col>
        </a-row>
        <div class="content" v-if="socialLogin">
          <p class="or">or</p>
        </div>
        <div class="center">
          <div class="social-auth" v-if="githubprovider">
            <a-button
              @click="handleGithubProviderAndDomain"
              tag="a"
              color="primary"
              :href="getGitHubUrl(from)"
              class="auth-btn github-auth"
              style="height: 38px; width: 185px; padding: 0; margin-bottom: 5px;" >
              <img src="/assets/github.svg" style="width: 32px; padding: 5px" />
              <a-text>Sign in with Github</a-text>
            </a-button>
          </div>
          <div class="social-auth" v-if="googleprovider">
            <a-button
              @click="handleGoogleProviderAndDomain"
              tag="a"
              color="primary"
              :href="getGoogleUrl(from)"
              class="auth-btn google-auth"
              style="height: 38px; width: 185px; padding: 0" >
              <img src="/assets/google.svg" style="width: 32px; padding: 5px" />
              <a-text>Sign in with Google</a-text>
            </a-button>
          </div>
        </div>
        <div style="display: flex; justify-content: space-between; margin-top: 40px;">
          <div>
            <a type="primary" @click="showModal">Termos de uso</a>
            <a-modal
            :visible="modal.open"
            v-model:open="modal.open"
            title="Termos de uso"
            width="70%"
            centered
            @cancel="handleCancel"
            :footer="null">
              <div>
                <div class="textsContainer">
                  <div class="downloadBlock">
                    <h3 style="font-size: 24px; margin-top: 0;">Download do documento</h3>
                    <a-button type="primary" shape="round" :size="size" href="assets/TERMOS_DE_USO_ALTATECH.pdf" target="_blank">
                      <template #icon>
                        <DownloadOutlined />
                      </template>
                      Download
                    </a-button>
                  </div>
                  <p>Atualização: 21 de Fevereiro de 2022</p> <br />
                  <ol style="padding-left: 0px;">
                    <li>Introdução</li>
                    <div id="paragrafo-inicial">
                      <p>
                        Bem-vindo aos CANAIS DIGITAIS da <strong>ALTATECH</strong>.
                        Obrigado por dedicar este tempo para ler os Termos e Condições
                        de Uso da <strong>ALTATECH</strong>.
                      </p>
                      <p>
                        A seguir, apresentamos a você (USUÁRIO) os Termos e Condições
                        de Uso (Termo), documento que relaciona as principais regras a
                        serem observadas por todos que acessarem os Canais Digitais da
                        <strong>ALTATECH</strong> (CANAIS DIGITAIS), bem como algumas
                        instruções para utilização dos serviços
                        <strong>ALTATECH</strong>, e que o ajudarão a utilizá-los.
                      </p>
                      <p>
                        O presente Termo tem por finalidade estabelecer regras sobre o
                        tratamento de referidos dados pessoais, dentro do escopo dos
                        serviços e funcionalidades dos CANAIS DIGITAIS oferecidos,
                        além do registro das atividades de acordo com as leis
                        aplicáveis em vigor, com transparência e clareza junto ao
                        USUÁRIO e ao mercado em geral.
                      </p>
                      <p>
                        Como condição para acesso e uso das funcionalidades exclusivas
                        de nossos CANAIS DIGITAIS, o USUÁRIO declara que, ao
                        aceitá-las ou ao utilizar os serviços da
                        <strong>ALTATECH</strong>, por livre e espontânea vontade, fez
                        a leitura completa e atenta do presente Termo, estando
                        plenamente ciente de todas as suas disposições, bem como das
                        demais políticas incorporadas ao presente Termo por
                        referência, conferindo, assim, sua livre e expressa
                        concordância com as regras aqui estipuladas.
                      </p>
                      <p>
                        <strong>SEÇÕES DO TERMO DE USO</strong> – Para facilitar,
                        acesse diretamente a seção de seu interesse, conforme seus
                        objetivos de esclarecimentos. Recomendamos periodicamente a
                        leitura cuidadosa e completa.
                      </p>
                      <p>
                        Para acessar algumas das funcionalidades disponibilizadas nos
                        CANAIS DIGITAIS, o USUÁRIO deverá, primeiro, realizar o seu
                        cadastro. Somente é permitido a cada USUÁRIO a realização de
                        um único cadastro junto aos CANAIS DIGITAIS da
                        <strong>ALTATECH</strong>. Sendo identificado mais de um
                        registro para o mesmo USUÁRIO, um ou todos esses cadastros
                        poderão ser suspensos ou cancelados, sem aplicação de
                        quaisquer penalidades à <strong>ALTATECH</strong>.
                      </p>
                      <p>
                        Quando o cadastro for de pessoa física, o USUÁRIO deverá ter,
                        no mínimo, 14 (quatorze) anos e, se menor de 18 (dezoito)
                        anos, ser assistido pelos pais ou responsáveis legais, devendo
                        esses serem responsáveis por todo e qualquer ato praticado
                        pelos menores quando da utilização dos CANAIS DIGITAIS. No
                        caso do cadastro de pessoa jurídica, o USUÁRIO deve ser o
                        representante legal da Empresa ou possuir poderes para
                        representá-la junto a <strong>ALTATECH</strong>.
                      </p>
                      <p>
                        O USUÁRIO que já tiver seu CPF cadastrado como pessoa física,
                        também poderá utilizar seu login para cadastro de pessoa
                        jurídica, podendo estar associado a mais de um CNPJ. O USUÁRIO
                        pessoa física que tiver seu login vinculado ao cadastro de
                        pessoa jurídica terá acesso aos extratos de pagamento,
                        extratos de transações, solicitações de serviços relacionados
                        à pessoa jurídica, dentre outras funcionalidades. Para
                        desvincular um login de USUÁRIO pessoa física do cadastro de
                        um USUÁRIO pessoa jurídica, o responsável pela pessoa jurídica
                        deverá entrar em contato com Central de Atendimento para
                        solicitar a exclusão do cadastro daquele USUÁRIO.
                      </p>
                      <p>
                        O cadastro do USUÁRIO está condicionado ao fornecimento de
                        seus dados pessoais e cadastrais, diretamente nos CANAIS
                        DIGITAIS. Para otimizar a experiência do USUÁRIO, a
                        <strong>ALTATECH</strong> poderá fazer o uso de aplicativos de
                        autenticação (&quot;login&quot;) disponibilizados pelas mídias
                        sociais, tais como, mas não se limitando, ao Facebook Connect
                        ou Google Sign-In.
                      </p>
                      <p>
                        A <strong>ALTATECH</strong> poderá recusar ou não aprovar
                        solicitações de cadastros realizados por USUÁRIOS que não
                        fornecerem os dados corretamente.
                      </p>
                      <p>
                        A <strong>ALTATECH</strong> possui a faculdade de se recusar a
                        conceder, suspender ou cancelar a Conta de Acesso de qualquer
                        USUÁRIO que a utilizar de forma fraudulenta, incorreta,
                        desatualizada ou incompleta, violar ou tentar violar o
                        presente Termo ou qualquer documento da
                        <strong>ALTATECH</strong> disponibilizado em nossos CANAIS
                        DIGITAIS. Essa previsão também se estende para os casos em que
                        a <strong>ALTATECH</strong> tiver razões suficientes para
                        suspeitar que tais informações sejam falsas, incorretas,
                        desatualizadas ou incompletas. A suspensão ou cancelamento dos
                        serviços independem do envio de notificação prévia pela
                        <strong>ALTATECH</strong> ao USUÁRIO.
                      </p>
                      <p>
                        A <strong>ALTATECH</strong> poderá alterar os critérios de
                        elegibilidade de acesso e utilização de nossos CANAIS DIGITAIS
                        a qualquer momento, portanto, o USUÁRIO deverá verificar com
                        frequência o Termo atualizado no site da
                        <strong>ALTATECH</strong>.
                      </p>
                    </div>
                    <li>Da Segurança</li>
                    <div id="da-seguranca">
                      <p>
                        Os CANAIS DIGITAIS utilizam barreiras de proteção e
                        procedimentos de segurança para salvaguardar os seus ativos, a
                        fim de evitar eventual mau uso ou alteração não autorizada dos
                        dados por ela custodiados.
                      </p>
                      <p>
                        O fornecimento e envio dos dados é de responsabilidade do
                        USUÁRIO. Somente após a coleta dos dados que a
                        <strong>ALTATECH</strong> conseguirá aplicar seus controles de
                        segurança e preservar a privacidade de seu USUÁRIO.
                      </p>
                      <p>
                        A <strong>ALTATECH</strong> adota diversos mecanismos de
                        segurança para garantir o acesso aos dados do USUÁRIO, entre
                        eles a senha de acesso do USUÁRIO nos CANAIS DIGITAIS.
                      </p>
                      <p>
                        O acesso às áreas transacionais dos CANAIS DIGITAIS é
                        realizado mediante o uso do login e senha, criada diretamente
                        pelo USUÁRIO conforme os requisitos de segurança indicados
                        pela <strong>ALTATECH</strong> no momento de seu cadastro.
                        Assim, a senha é a credencial de acesso aos CANAIS DIGITAIS,
                        sendo rigorosamente definida e gerenciada pelo próprio
                        USUÁRIO, sendo de utilização individual e intransferível.
                      </p>
                    </div>
                    <li>RESPONSABILIDADES E OBRIGAÇÕES DOS USUÁRIOS</li>
                    <div id="responsabilidades-usuario">
                      <p>Os USUÁRIOS são responsáveis e se obrigam a:</p>
                      <p>
                        Utilizar corretamente, de acordo o previsto na lei e nos
                        princípios éticos, os CANAIS DIGITAIS disponibilizados pela
                        <strong>ALTATECH</strong>, respeitando as condições que regem
                        a sua utilização e finalidade;
                        <br /> Fornecer seus dados cadastrais corretos, completos e
                        atualizados, além de informar o canal de contato apto a ser
                        acionado pela <strong>ALTATECH</strong> para o melhor
                        cumprimento de seus serviços, por si ou por intermédio de seu
                        empregador. Se, a qualquer momento, for constatado que o
                        USUÁRIO forneceu dados falsos ou não condizentes com a
                        realidade, a <strong>ALTATECH</strong> se reserva ao direito
                        de suspender ou cancelar seu cadastro, sem prejuízo de adotar
                        as medidas que entender cabíveis;
                        <br /> Dispor de dispositivos e equipamentos compatíveis,
                        serviço de conexão à Internet com antivírus e firewall
                        habilitados, softwares devidamente atualizados;
                        <br /> Manter o sigilo dos dados de acesso a sua Conta de
                        Acesso, em especial o seu login e senha, em relação a
                        terceiros e utilizá-la de modo individual e intransferível,
                        não disponibilizando, divulgando ou compartilhando sua senha
                        ou qualquer mecanismo de autenticação com quem quer que seja,
                        inclusive em Plataformas oferecidas por terceiros. Caso
                        suspeite que a confidencialidade de sua senha foi violada, o
                        USUÁRIO deverá proceder sua troca ou atualização o mais rápido
                        possível ou contatar os canais de atendimento disponíveis;
                        <br /> Adotar senha forte e não utilizar sequências óbvias de
                        números e letras ou de fácil dedução. Em adição, recomendamos
                        que o USUÁRIO não reutilize a mesma senha em aplicativos,
                        portais e sites de terceiros;
                        <br /> Adotar medidas em seus dispositivos tecnológicos para
                        evitar o acesso físico e lógico por terceiros não autorizados,
                        tais como utilização de senha e/ou biometria;
                        <br /> Reconhecer que todos os acessos e operações realizadas
                        após sua autenticação digital bem-sucedida são interpretados
                        como sendo de sua responsabilidade de forma incontestável,
                        inclusive aqueles derivados de uso indevido ou divulgação
                        desta para terceiros;
                        <br />
                        Deixar seus sistemas de AntiSpam, filtros similares ou
                        configurações de redirecionamento de mensagens ajustados, de
                        modo que não interfiram no recebimento dos comunicados e
                        materiais dos CANAIS DIGITAIS, não sendo aceitável nenhuma
                        escusa caso não tenha tido acesso a algum e-mail ou mensagem
                        eletrônica em virtude dos recursos mencionados. Respeitar
                        todos os direitos de propriedade intelectual referentes aos
                        CANAIS DIGITAIS que são de titularidade da
                        <strong>ALTATECH</strong>, incluindo todos os direitos
                        referentes a terceiros que, porventura, estejam, ou estiveram,
                        de alguma forma ali disponíveis. Da mesma forma, os USUÁRIOS
                        somente poderão reproduzir os conteúdos disponíveis nos CANAIS
                        DIGITAIS, em especial o nome empresarial, denominação social,
                        marcas depositadas ou registradas, bem como quaisquer outros
                        sinais distintivos e bens de propriedade intelectual de
                        titularidade da <strong>ALTATECH</strong> ou de qualquer
                        empresa de seu grupo econômico, caso tenham sido expressamente
                        autorizados, por escrito, pela <strong>ALTATECH</strong> neste
                        sentido;
                        <br /> Não acessar áreas de programação dos CANAIS DIGITAIS,
                        seu banco de dados, códigos fonte ou qualquer outro conjunto
                        de dados disponíveis nestes ambientes;
                        <br /> Não realizar ou permitir engenharia reversa, nem
                        traduzir, descompilar, copiar, modificar, reproduzir, alugar,
                        sublicenciar, publicar, divulgar, transmitir, emprestar,
                        distribuir ou, de outra maneira, dispor inapropriadamente das
                        funcionalidades dos CANAIS DIGITAIS;
                        <br /> Não utilizar softwares para automatização de acessos em
                        nossos CANAIS DIGITAIS, com exceção das bibliotecas
                        disponibilizadas pela <strong>ALTATECH</strong>, bem como
                        softwares de mineração ou coleta automatizada de dados, de
                        qualquer tipo ou espécie, além de outro aqui não tipificado
                        que atue de modo similar.
                      </p>
                      <p>
                        As funcionalidades que compõem os CANAIS DIGITAIS são
                        oferecidas como ferramentas para apoiar os serviços por ela
                        oferecidos, não conferindo ao USUÁRIO nenhum direito sobre os
                        CANAIS DIGITAIS ou sobre as estruturas tecnológicas que as
                        sustentam.
                      </p>
                      <p>
                        A eventual remoção, bloqueio ou suspensão de qualquer conteúdo
                        ou funcionalidade nos CANAIS DIGITAIS em decorrência de alguma
                        reclamação, deverá ser sempre compreendida como demonstração
                        de boa-fé e intenção de solução amigável de conflitos, jamais,
                        como reconhecimento de culpa ou de qualquer infração pela
                        <strong>ALTATECH</strong> a direito de terceiro.
                      </p>
                      <p>
                        Na incidência de danos à <strong>ALTATECH</strong> ou a
                        terceiros, o responsável se compromete a arcar com todas as
                        obrigações de indenizar o sujeito lesado, inclusive aqueles
                        cuja origem for de atos praticados por meio de sua Conta de
                        Acesso, assumindo o polo passivo de ação judicial ou
                        procedimento administrativo e requerendo a exclusão da
                        <strong>ALTATECH</strong>, devendo arcar totalmente com as
                        despesas e custas processuais atinentes, deixando-a livre de
                        prejuízos e ônus.
                      </p>
                      <p>
                        Os comentários enviados pelos USUÁRIOS e publicados nos CANAIS
                        DIGITAIS não deverão ser interpretados como opinião da
                        <strong>ALTATECH</strong>, tampouco declaração de concordância
                        com o que foi publicado. Os USUÁRIOS são inteiramente
                        responsáveis pelos comentários que submeterem nos CANAIS
                        DIGITAIS.
                      </p>
                      <p>
                        O descumprimento de quaisquer das obrigações aqui estipuladas
                        poderá acarretar a suspensão total ou parcial das
                        funcionalidades nos CANAIS DIGITAIS, ou exclusão da Conta de
                        Acesso, sem aviso prévio, conforme previsto neste Termo.
                      </p>
                    </div>
                    <li>RESPONSABILIDADE E OBRIGAÇÕES DA ALTATECH</li>
                    <div id="responsabilidades-altatech">
                      <p>
                        Os CANAIS DIGITAIS e suas funcionalidades são apresentadas aos
                        USUÁRIOS na maneira como estão disponíveis, podendo passar por
                        constantes aprimoramentos e atualizações, obrigando-se a
                        <strong>ALTATECH</strong> a:
                      </p>
                      <p>
                        Preservar a sua funcionalidade, com links não quebrados e
                        utilizando layout que respeita a usabilidade e navegabilidade,
                        sempre que possível; Exibir as funcionalidades de maneira
                        clara, completa, precisa e suficiente de modo que exista a
                        exata percepção das operações realizadas; Proteger, por meio
                        do estado da técnica disponível, os dados coletados pelas
                        funcionalidades disponibilizadas.
                      </p>
                      <p>
                        A <strong>ALTATECH</strong> envida seus esforços para a
                        manutenção da disponibilidade contínua e permanente de nossos
                        CANAIS DIGITAIS. No entanto, pode ocorrer, eventualmente,
                        alguma indisponibilidade temporária decorrente de manutenção
                        necessária ou mesmo gerada por motivo de força maior, como
                        desastres naturais, falhas nos sistemas de comunicação e
                        acesso à Internet ou fatos de terceiro que fogem de sua esfera
                        de vigilância e responsabilidade.
                      </p>
                      <p>
                        Se isso ocorrer, a <strong>ALTATECH</strong> fará o que
                        estiver ao seu alcance para restabelecer o acesso aos CANAIS
                        DIGITAIS o mais breve possível, dentro das limitações técnicas
                        de seus serviços e serviços de terceiros, dos quais depende
                        para ficar online.
                      </p>
                      <p>
                        Eventuais procedimentos de manutenção que acarretem a
                        indisponibilidade dos CANAIS DIGITAIS por longos períodos
                        serão informados por meio de canais oficiais de comunicação,
                        por exemplo, perfis oficiais em mídias sociais, como e-mails,
                        perfis oficiais em mídias sociais ou Central de Atendimento.
                      </p>
                      <p>
                        O USUÁRIO não possui qualquer direito para exigir a
                        disponibilidade dos CANAIS DIGITAIS, tampouco pleitear
                        indenização ou reparação de danos no caso de nossos CANAIS
                        DIGITAIS permanecerem fora do ar, independentemente da
                        motivação.
                      </p>
                      <p>
                        A <strong>ALTATECH</strong> pode, de acordo com seus objetivos
                        de negócio, modificar ou descontinuar (temporária ou
                        permanentemente) a distribuição ou a atualização de nossos
                        CANAIS DIGITAIS.
                      </p>
                    </div>
                    <li>ISENÇÃO E LIMITAÇÕES DE RESPONSABILIDADE ALTATECH.</li>
                    <div id="isencao-limitacoes-altatech">
                      <p>
                        A <strong>ALTATECH</strong> não se responsabiliza:
                      </p>
                      <p>
                        Por quaisquer problemas, bugs, Glitches ou funcionamentos
                        indevidos que ocorrerem nos dispositivos e equipamentos dos
                        USUÁRIOS e sejam resultantes, direta ou indiretamente, do uso
                        regular de nossos CANAIS DIGITAIS;
                        <br /> Por qualquer dano direto ou indireto ocasionado por
                        eventos de terceiros, a exemplo, mas não se limitando a,
                        ataque de hackers, falhas no sistema, no servidor ou na
                        conexão à internet, inclusive por ações de softwares que
                        possam, de algum modo, danificar ativos físicos ou lógicos ou
                        a conexão dos USUÁRIOS em decorrência do acesso, utilização ou
                        navegação em nossos CANAIS DIGITAIS, bem como a transferência
                        de dados, arquivos, imagens, textos, áudios ou vídeos contidos
                        neste;
                        <br /> Pela navegação dos USUÁRIOS nos links externos
                        disponibilizados nos CANAIS DIGITAIS, sendo de livre escolha
                        do USUÁRIO o uso de tais links, além de ser seu dever a
                        leitura dos respectivos Termos e Condições de Uso e Política
                        de Privacidade do recurso acessado e agir conforme neles
                        determinado Por verificar, controlar, aprovar ou garantir a
                        adequação ou exatidão das informações ou dados
                        disponibilizados em tais links, não sendo, portanto,
                        responsáveis por prejuízos, perdas ou danos ocorridos pela
                        visita de tais sites, cabendo ao interessado verificar a
                        confiabilidade das informações e dados ali exibidos antes de
                        tomar alguma decisão ou praticar algum ato.
                      </p>
                    </div>
                    <li>APLICAÇÕES DE TERCEIROS</li>
                    <div id="aplicacoes-de-terceiros">
                      <p>
                        Nossos CANAIS DIGITAIS podem conter links ou frames para sites
                        e/ou aplicativos de terceiros, que podem ou não ser parceiros
                        da <strong>ALTATECH</strong>. Para mais informações, consultar
                        a Política de Privacidade da <strong>ALTATECH</strong>.
                      </p>
                      <p>
                        Essas aplicações de terceiros são disponibilizadas buscando,
                        tão somente, proporcionar mais um benefício e comodidade para
                        o USUÁRIO. Dentro destes sites e/ou aplicativos de terceiros,
                        o USUÁRIO estará sujeito a outros termos de uso e a outras
                        políticas de privacidade, que também deverão ser observados
                        pelo USUÁRIO.
                      </p>
                      <p>
                        A inclusão desses links e frames não significa que a
                        <strong>ALTATECH</strong> tenha conhecimento, controle,
                        concorde ou seja responsável por eles ou por seus respectivos
                        conteúdos, segurança e proteção de dados. Sendo assim, a
                        <strong>ALTATECH</strong> não se responsabiliza por eventuais
                        perdas e danos sofridos em razão de utilização, pelo USUÁRIO,
                        dos referidos links ou frames.
                      </p>
                    </div>
                    <li>OUTRAS POLÍTICAS RELACIONADAS</li>
                    <div id="outras-politicas-relacionads">
                      <p>
                        Em caso de dúvidas sobre a proteção a dados pessoais ou para
                        obter mais informações sobre dados pessoais, por favor,
                        <strong>[ politica de privacidade]</strong>
                      </p>
                    </div>
                    <li>
                      SOBRE CANAL DIGITAL ALTATECH – CONDIÇÕES ESPECÍFICAS DE USO
                    </li>
                    <div>
                      <p>
                        A <strong>ALTATECH</strong> é um portal de relacionamento com
                        o USUÁRIO, onde é possível ter acesso a cursos, treinamentos,
                        webinars e ferramentas de gestão e TI, entre outros serviços,
                        sendo, também, um CANAL DIGITAL de promoções que disponibiliza
                        ofertas de produtos e/ou serviços das empresas parceiras
                        (Parceiros) da <strong>ALTATECH</strong>, com preços e/ou
                        condições de compra mais atrativos e/ou outras promoções que
                        venham a ser divulgadas.
                      </p>
                      <p>
                        As facilidades estão disponíveis a todos os USUÁRIOS
                        <strong>ALTATECH</strong>
                        (se pessoa física é um usuário) que efetuarem o respectivo
                        cadastro por meio dos CANAIS DIGITAIS e consentirem
                        expressamente com as condições do presente Termo. A aquisição
                        dos produtos e/ou serviços ofertados, bem como a participação
                        em outras promoções é realizada diretamente pelo próprio
                        USUÁRIO junto aos Parceiros da <strong>ALTATECH</strong>.
                      </p>
                      <p>
                        A cada oferta e/ou promoção, a <strong>ALTATECH</strong> e
                        seus parceiros poderão determinar regras próprias de
                        participação, que serão divulgadas nas respectivas páginas dos
                        CANAIS DIGITAIS.
                      </p>
                      <p>
                        As ofertas e/ou promoções somente poderão ser utilizadas
                        dentro do seu prazo de validade, não cabendo qualquer forma de
                        indenização após o período determinado de participação para
                        cada uma delas. Descontos aplicados pelos Parceiros podem
                        sofrer alterações pela quantidade de estoque, entrega e/ou
                        meio de pagamento escolhido pelo USUÁRIO.
                      </p>
                      <p>
                        O USUÁRIO está ciente de que o <strong>ALTATECH</strong> não
                        detém a posse nem propriedade dos produtos e/ou serviços
                        ofertados em nossos CANAIS DIGITAIS, e que as promoções são
                        realizadas em nome do respectivo Parceiro, devendo o USUÁRIO
                        sempre confirmar as informações diretamente com o Parceiro na
                        aquisição dos produtos e/ou serviços e ofertas. Os prazos e
                        formas de entrega também devem ser negociados diretamente com
                        o Parceiro escolhido pelo USUÁRIO.
                      </p>
                      <p>
                        A forma e meio de pagamento são determinados por cada Parceiro
                        e o USUÁRIO deverá verificar junto aos Parceiros as formas e
                        meios de pagamento disponíveis. As formas de pagamento nem
                        sempre constam nas regras da promoção e/ou oferta na
                        <strong>ALTATECH</strong> e devem ser confirmadas diretamente
                        ou no site do parceiro.
                      </p>
                      <p>
                        Os conteúdos disponibilizados por Parceiros da
                        <strong>ALTATECH</strong> são de responsabilidade única
                        destes. Assim, se a <strong>ALTATECH</strong> detectar nestes
                        conteúdos qualquer conteúdo ilícito ou em contrariedade a este
                        Termo ou as regras próprias de participação, ficará a seu
                        critério a remoção do conteúdo e a notificação às autoridades
                        competentes.
                      </p>
                      <p>
                        A <strong>ALTATECH</strong> não se responsabiliza pelas
                        descrições dos serviços e/ou produtos das promoções e ofertas,
                        uma vez que são apresentadas exclusivamente pelos Parceiros.
                        As informações relativas a tais produtos e/ou serviços e
                        ofertas, seus nomes comerciais, marcas ou sinais distintivos,
                        contidos em nossos CANAIS DIGITAIS, dizem respeito às
                        informações reais prestadas pelos Parceiros e têm a intenção
                        de prestar a maior quantidade de informações verdadeiras e
                        exatas sobre os produtos e/ou serviços e suas respectivas
                        características. As imagens e/ou vídeos relativos aos produtos
                        e/ou serviços podem ser meramente ilustrativas. É importante
                        confirmar a descrição dos produtos e/ou serviços.
                      </p>
                    </div>
                    <li>DISPOSIÇÕES GERAIS</li>
                    <div id="disposicoes-gerais">
                      <p>
                        O atendimento aos USUÁRIOS poderá ser realizado por meio dos
                        canais de atendimento disponibilizados em cada um dos CANAIS
                        DIGITAIS. Os USUÁRIOS também poderão se valer dos canais de
                        atendimento toda vez que presenciarem ou verificarem conteúdo
                        impróprio nos CANAIS DIGITAIS, seja ele notadamente ilícito ou
                        contrário às regras de uso aqui estipuladas.
                      </p>
                      <p>
                        Para deixar de receber as campanhas promocionais e
                        solicitações de participação em pesquisas por meios
                        eletrônicos, como e-mail ou SMS, é necessário que o USUÁRIO
                        faça o cancelamento diretamente na área de cadastro dos CANAIS
                        DIGITAIS.
                      </p>
                      <p>
                        O presente Termo está sujeito a constante melhoria e
                        aprimoramento. Assim, a <strong>ALTATECH</strong> se reserva o
                        direito de atualizá-lo a qualquer momento, conforme sua
                        finalidade ou conveniência, tal qual para adequação e
                        conformidade legal de disposição de lei ou norma que tenha
                        força jurídica equivalente, cabendo aos USUÁRIOS verificá-lo
                        sempre que efetuar o acesso nos CANAIS DIGITAIS. As
                        atualizações deste Termo entrarão em vigor na data de sua
                        publicação, a não ser que seja informada outra data de início.
                      </p>
                      <p>
                        Ocorrendo atualizações neste Termo, a
                        <strong>ALTATECH</strong> notificará o USUÁRIO mediante as
                        ferramentas disponíveis nos CANAIS DIGITAIS. Ao navegar pelos
                        CANAIS DIGITAIS e utilizar suas funcionalidades, o USUÁRIO
                        aceita guiar-se pelas condições do Termos que se encontram
                        vigentes na data de seu acesso. Por isso, é recomendável que o
                        USUÁRIO se mantenha atualizado.
                      </p>
                      <p>
                        A tolerância do eventual descumprimento de quaisquer das
                        condições do presente Termo não constituirá novação das
                        obrigações aqui estipuladas e tampouco impedirá ou inibirá a
                        exigibilidade das mesmas a qualquer tempo.
                      </p>
                      <p>
                        Caso alguma disposição deste Termo seja julgada inaplicável ou
                        sem efeito, o restante do presente Termo continua a viger, sem
                        a necessidade de medida judicial que declare tal assertiva.
                      </p>
                      <p>
                        O USUÁRIO reconhece que toda comunicação realizada por e-mail
                        ou SMS (aos endereços informados no seu cadastro) também são
                        válidas, eficazes e suficientes para a divulgação de qualquer
                        assunto que se refere aos serviços prestados pela
                        <strong>ALTATECH</strong>, bem como às condições de sua
                        prestação ou a qualquer outro assunto nele abordado,
                        ressalvadas as disposições expressamente diversas previstas
                        neste Termo. Os CANAIS DIGITAIS utilizam como base a data e
                        horário oficial de Brasília.
                      </p>
                      <p>
                        Declarações do USUÁRIO: Sem prejuízo das demais disposições
                        deste Termo, o USUÁRIO declara e garante que as utilizações
                        por ele realizadas nos CANAIS DIGITAIS não serão utilizadas
                        para, mas sem limitação a:
                      </p>
                      <section>
                        <ol type="I">
                          <li>Infração à legislação em vigor; e</li>
                          <li>
                            Qualquer tipo de atividade que possa ser considerada
                            fraudulenta ou ilegal.
                          </li>
                        </ol>
                      </section>
                      <p>
                        Desde já, o USUÁRIO concorda em isentar, defender e manter a
                        <strong>ALTATECH</strong>, bem como seus respectivos
                        diretores, agentes, sócios, funcionários e parceiros, isentos
                        de quaisquer prejuízos, responsabilidades, ações judiciais ou
                        demandas, incluindo honorários advocatícios, devidos ou
                        decorrentes do uso dos CANAIS DIGITAIS ou de violação deste
                        Termo.
                      </p>
                      <p>
                        Este Termo está integralmente disponível para consulta nos
                        CANAIS DIGITAIS podendo ser acessado pelo USUÁRIO a qualquer
                        momento.
                      </p>
                    </div>
                    <li>LEI APLICÁVEL E JURISDIÇÃO</li>
                    <div id="lei-aplicavel-jurisdicao">
                      <p>
                        A presente Política de Privacidade será regida, interpretada e
                        executada segundo a legislação brasileira, no idioma
                        português, sendo eleito o Foro da Comarca de domicílio do
                        usuário para dirimir qualquer litígio ou controvérsia
                        envolvendo o presente documento, salvo ressalva específica de
                        competência pessoal, territorial ou funcional pela legislação
                        aplicável. Fora do Estado a JUSPRO – Justiça Sem Processo
                        Mediação e Conciliação S/S Ltda. como Câmara de Mediação apta
                        a dirimir as questões envolvendo os Usuários titulares dos
                        dados, nos termos do artigo 52, §7º da Lei Geral de Proteção
                        de Dados.
                      </p>
                    </div>
                    <li>DEFINIÇÕES UTILIZADAS NO TERMO</li>
                    <div id="definicoes-termo">
                      <p>
                        Para os fins deste Termo, devem se considerar as seguintes
                        definições e descrições para seu melhor entendimento:
                      </p>
                      <p>
                        <strong>AntiSpam:</strong> Sistema que evita correspondências
                        não desejadas, como publicidade em massa, pelo bloqueio de
                        mensagens ou as movendo para pasta específica.
                      </p>
                      <p>
                        <strong>Bugs:</strong> Utilizado quando alguma falha lógica
                        impede a execução de alguma operação na Plataforma.
                      </p>
                      <p>
                        <strong>Cloud Computing:</strong> Ou computação em nuvem, é
                        tecnologia de virtualização de serviços construída a partir da
                        interligação de mais de um servidor por meio de rede de
                        informação comum (p. ex. a Internet), com objetivo de aumentar
                        a disponibilidade dos serviços sustentados.
                      </p>
                      <p>
                        <strong>Conta de Acesso:</strong> Credencial de um USUÁRIO
                        cadastrado, que permite o acesso às funcionalidades exclusivas
                        dos CANAIS DIGITAIS, que é definida pelo nome de usuário e
                        senha.
                      </p>
                      <p>
                        <strong>Glitches:</strong> Utilizado para designar um
                        funcionamento inesperado que impede a execução ou o bom
                        funcionamento da operação pretendida nos CANAIS DIGITAIS.
                      </p>
                      <p>
                        <strong>IP:</strong> Abreviatura de Internet Protocol. É um
                        conjunto de números que identifica a conexão de acesso à
                        Internet utilizada pelos USUÁRIOS para acessar os CANAIS
                        DIGITAIS.
                      </p>
                      <p>
                        <strong>Layout:</strong> Conjunto compreendido entre
                        aparência, design e fluxos dos CANAIS DIGITAIS.
                      </p>
                      <p>
                        <strong>Link:</strong> Terminologia para endereço de internet.
                      </p>
                      <p>
                        <strong>Logs ou registros de acesso:</strong> Registros de
                        atividades dos USUÁRIOS efetuadas nos CANAIS DIGITAIS.
                      </p>
                      <p>
                        <strong>CANAIS DIGITAIS:</strong> Representam os Portais,
                        Aplicativos, Ferramentas de Comunicação e Perfis nas mídias
                        sociais oficiais da <strong>ALTATECH</strong>.
                      </p>
                      <p>
                        <strong>Session ID:</strong> Identificação da sessão do
                        USUÁRIO ao acessar um dos CANAIS DIGITAIS.
                      </p>
                      <p>
                        <strong>ALTATECH:</strong> <strong>ALTATECH</strong>, inscrita
                        no CNPJ nº 05.365.132/0001-30, - com sede na AV Jucelino
                        Kubitschek no 1905 Terreo Vila Ipiranga, Londrina – PR CEP.:
                        86.010-540.
                      </p>
                      <p>
                        <strong>USUÁRIO:</strong> Pessoa Física, na condição de
                        USUÁRIO, e/ou Responsável por Pessoa Jurídica (Clientes e
                        Estabelecimentos Credenciados) que acessa ou interage com as
                        atividades oferecidas pelas Plataformas da
                        <strong>ALTATECH</strong>.
                      </p>
                    </div>
                  </ol>
                </div>
              </div>
            </a-modal>
          </div>
          <div>
            <a type="second" @click="showModalPrivacy">Politica de privacidade</a>
            <a-modal
            :visible="modal.policyPrivacyOpen"
            v-model:open="modal.policyPolicyOpen"
            title="Politica de privacidade"
            width="70%"
            centered
            @cancel="handleCancel"
            :footer="null">
              <div>
                <div class="textsContainer">
                  <div class="downloadBlock">
                    <h3 style="font-size: 24px; margin-top: 0px;">Download do documento</h3>
                    <a-button type="primary" shape="round" :size="size" href="assets/POLITICAS_DE_PRIVACIDADE_ALTATECH.pdf" target="_blank">
                      <template #icon>
                        <DownloadOutlined />
                      </template>
                      Download
                    </a-button>
                  </div>
                  <p>Atualização: 21 de Fevereiro de 2022</p>
                  <div id="paragrafo-inicial">
                    <p>
                      A <strong>ALTATECH</strong> constrói relacionamentos fortes e
                      duradouros com seus clientes, parceiros, usuários e associados,
                      com base na confiança mútua. Portanto, assegurar que seus Dados
                      Pessoais (conforme definido abaixo) estejam seguros e permaneçam
                      em sigilo é uma prioridade absoluta para a
                      <strong>ALTATECH</strong>.
                    </p>
                    <p>
                      Os termos dessa Política se aplicam aos Usuários e/ou Visitantes
                      dos websites da www.altatech.com.br (doravante denominado
                      &quot;site&quot;) e/ou do aplicativo móvel
                      <strong>ALTATECH</strong> (doravante denominado
                      &quot;App&quot;), também conhecidos como CANAIS DIGITAIS.
                    </p>
                    <p>
                      A <strong>ALTATECH</strong>, por valorizar e zelar pela
                      privacidade e proteção dos Dados Pessoais solicitados, aplica
                      uma Política muito rigorosa para resguardar a proteção dos Dados
                      Pessoais daqueles que utilizam seu site e/ou App. Reconhecemos a
                      importância da privacidade, da transparência, da segurança e da
                      proteção de dados para nossos clientes e para todos os titulares
                      de dados que possam ter seus Dados Pessoais tratados por meio
                      dos sistemas da <strong>ALTATECH</strong>.
                    </p>
                    <p>
                      <strong>SEÇÕES DESTA POLÍTICA</strong> – Para facilitar, acesse
                      diretamente a seção de seu interesse, conforme seus objetivos de
                      esclarecimentos. Recomendamos periodicamente a leitura cuidadosa
                      e completa dessa Política.
                    </p>
                  </div>
                  <ol>
                    <li>OBJETIVO DESTA POLÍTICA</li>
                    <div id="objetivo-politica">
                      <p>
                        A <strong>ALTATECH</strong> emprega os melhores esforços para
                        fornecer medidas de proteção adequadas em todas as suas
                        operações e para implementar as políticas e os procedimentos
                        mais consistentes, efetivos e rigorosos. Por reconhecer a
                        importância da sua privacidade, desenvolvemos esta Política
                        para informá-lo a respeito das condições sob as quais Tratamos
                        e Protegemos seus Dados Pessoais. Esta Política também
                        descreve seus direitos e como você pode entrar em contato
                        conosco para exercê-los ou nos fazer quaisquer perguntas que
                        você possa ter sobre a proteção de seus Dados Pessoais.
                      </p>
                      <p>
                        Ao aceitar o conteúdo desta Política, você está ciente de que
                        a <strong>ALTATECH</strong> é a Controladora dos seus Dados
                        Pessoais e declara e concorda que nós podemos tratar os seus
                        Dados Pessoais em conformidade com os termos aqui
                        estabelecidos, bem como manifesta ciência de que esta Política
                        pode ser modificada, a qualquer tempo, com todas as
                        atualizações publicadas nesta página. Por isso, recomendamos
                        que leia atentamente toda nossa Política. Caso não esteja de
                        acordo com as disposições, pedimos para que você interrompa
                        imediatamente o acesso e uso do site e/ou do App.
                      </p>
                      <p>
                        Esta Política é parte integrante dos Termos e Condições de Uso
                        do site, App e Serviços <strong>ALTATECH</strong> para
                        conhecer os
                        <a id="termos-de-uso-link" href="/info/termos-de-uso">
                          Termos e Condições de Uso
                        </a>
                        .
                      </p>
                    </div>
                    <li>DEFINIÇÕES</li>
                    <div id="definicoes">
                      <p>
                        Para os fins desta Política, devem ser consideradas as
                        seguintes definições, quando referidas em letra maiúscula ou
                        minúscula, plural ou singular, com ou sem negrito, para sua
                        correta compreensão:
                      </p>
                      <p>
                        &quot;Dados Pessoais&quot;: Significa qualquer informação
                        relacionada a uma pessoa natural identificada ou
                        identificável. Isso significa que um dado é considerado
                        pessoal quando ele permite a identificação direta ou indireta
                        da pessoa natural, como por exemplo: nome, sobrenome, data de
                        nascimento, telefone, e-mail, etc.
                      </p>
                      <p>
                        &quot;LGPD&quot;: Lei Geral de Proteção de Dados Pessoais (Lei
                        n.º 13.709, de 14 de agosto de 2018).
                      </p>
                      <p>
                        &quot;Tratamento&quot;: Toda a operação realizada com Dados
                        Pessoais, como as que se referem a coleta, produção, recepção,
                        classificação, utilização, acesso, reprodução, transmissão,
                        distribuição, processamento, arquivamento, armazenamento,
                        eliminação, avaliação ou controle da informação, modificação,
                        comunicação, transparência, difusão ou extração.
                      </p>
                      <p>
                        &quot;Bases Legais&quot;: Bases legais são as hipóteses legais
                        previstas na LGPD que autorizam a <strong>ALTATECH</strong> a
                        tratar Dados Pessoais.
                      </p>
                      <p>
                        &quot;Nós ou nosso&quot;: <strong>ALTATECH GROUP</strong>,
                        doravante denominada <strong>ALTATECH</strong>.
                      </p>
                      <p>
                        &quot;Você&quot;: Qualquer Pessoa Física, na condição de
                        visitante e/ou Usuário dos Serviços <strong>ALTATECH</strong>
                        e/ou responsável por Pessoa Jurídica (Clientes e/ou
                        Estabelecimentos Credenciados) que acesse ou interage com as
                        atividades e/ou Funcionalidades disponibilizadas nos CANAIS
                        DIGITAIS.
                      </p>
                      <p>
                        &quot;Usuário&quot;: São os colaboradores e/ou Usuários
                        indicados pelas empresas contratantes dos serviços
                        <strong>ALTATECH</strong> para utilização dos serviços
                        <strong>ALTATECH</strong>.
                      </p>
                      <p>
                        &quot;Controlador&quot;: Pessoa natural ou jurídica, de
                        direito público ou privado, a quem competem as decisões
                        referentes ao Tratamento de Dados Pessoais.
                      </p>
                    </div>
                    <li>IDENTIDADE E DETALHES DE CONTATO DO CONTROLADOR</li>
                    <div id="identidade-controlador">
                      <p>
                        A <strong>ALTATECH</strong>, doravante
                        <strong>ALTATECH</strong> inscrita no CNPJ nº
                        05.365.132/0001-30 com sede na AV Jucelino Kubitschek no 1905
                        Terreo Vila Ipiranga, Londrina – PR CEP.: 86.010-540 é
                        controladoras dos dados Pessoais.
                      </p>
                    </div>
                    <li>ORIGEM DOS DADOS PESSOAIS</li>
                    <div id="origem-dados-pessoais">
                      <p>
                        A <strong>ALTATECH</strong> coleta e trata os seus Dados
                        Pessoais para que você possa realizar o seu cadastro nos
                        CANAIS DIGITAIS e utilizar suas funcionalidades. Tais Dados
                        Pessoais poderão ser fornecidos:
                      </p>
                      <section>
                        <ol type="I">
                          <li>
                            Por você mesmo, ao se cadastrar ou ao fornecer informações
                            ao utilizar o site e/ou App;
                          </li>
                          <li>
                            Pela empresa que contrata os serviços da
                            <strong>ALTATECH</strong> em seu benefício;
                          </li>
                          <li>
                            Por coleta automática, ao navegar no site e/ou App; e
                          </li>
                          <li>
                            Por coleta de informações por meio de outras fontes
                            confiáveis.
                          </li>
                        </ol>
                      </section>
                      <h4>
                        Dados pessoais fornecidos pela empresa contratante ou por
                        Você:
                      </h4>
                      <p>
                        Os Dados Pessoais são coletados primordialmente pelo envio de
                        solicitações de pedido de serviços pela sua empresa
                        empregadora e/ou pela empresa que indicou seu nome para
                        recebimento dos serviços <strong>ALTATECH</strong> ou
                        fornecidos por você em seu cadastro.
                      </p>
                      <h4>As informações coletadas pela ALTATECH incluem:</h4>
                      <section>
                        <ol type="a">
                          <li>
                            Sendo pessoa física: nome, CPF, RG, e-mail, número de
                            telefone e celular, endereço;
                          </li>
                          <li>
                            Sendo pessoa jurídica: razão social, CNPJ, nome do
                            representante legal, RG e CPF do representante legal,
                            número de funcionários, endereço completo e telefone.
                          </li>
                        </ol>
                      </section>
                      <p></p>
                      <p></p>
                      <p>
                        Os Dados Pessoais identificados por um asterisco (*) nos
                        formulários de coleta de dados são de preenchimento
                        obrigatório, para que possamos atender às suas solicitações
                        e/ou prestar os serviços que forem solicitados. Caso você não
                        forneça esses Dados Pessoais, é possível que o acesso aos
                        nossos serviços seja restrito, ou mesmo inviabilizado.
                      </p>
                      <p>
                        Dados Pessoais coletados automaticamente: Coletamos algumas
                        informações automaticamente quando você visita o nosso site, a
                        fim de personalizar e aprimorar sua experiência. Coletamos
                        estas informações utilizando vários métodos, tais como:
                      </p>
                      <h4>Cookies</h4>
                      <p>
                        Podemos utilizar o recurso de &quot;Cookies&quot; como forma
                        de coletar automaticamente informações que nos permitem
                        identificar funcionalidades nas quais você possa ter o maior
                        interesse, a fim de melhorar o desempenho de nossas aplicações
                        e propiciar a você uma experiência de navegação mais
                        personalizada.
                      </p>
                      <p>
                        Contudo, saiba que se você desativar os &quot;cookies&quot; em
                        seu navegador, não terá uma experiência completa em Nosso
                        site. Por exemplo, você não poderá se beneficiar do início de
                        sessão automático e outras características de personalização
                        do Nosso site.
                      </p>
                      <p>
                        Observe também que você não deve se esquecer de configurar
                        todos os navegadores em seus diferentes dispositivos (tablets,
                        smartphones, computadores). Existem diversas formas de
                        configurar navegadores, portanto consulte a seção
                        &quot;Ajuda&quot; do navegador que você estiver usando para
                        entender como configurá-lo e colocar suas opções.
                      </p>
                      <p>
                        Você pode também desativar os cookies de medição de audiência
                        conforme links abaixo:
                      </p>
                      <p>
                        Você pode desativar os cookies &quot;Google Analytics&quot; ao
                        fazer o download do módulo acessível no seguinte endereço:
                        <a
                          id="tools-google-link"
                          href="https://tools.google.com/dlpage/gaoptout/"
                        >
                          https://tools.google.com/dlpage/gaoptout/
                      </a>
                        .
                      </p>
                      <p>
                        Você pode desativar o Insight Tag do LinkedIn ao modificar as
                        configurações de usuário no LinkedIn:
                        <a
                          id="linkedin-setting-link"
                          href="https://www.linkedin.com/psettings/"
                        >
                          https://www.linkedin.com/psettings/
                      </a>
                        .
                      </p>
                      <h4>Endereços Internet Protocol (&quot;IP&quot;)</h4>
                      <p>
                        Um endereço IP é um identificador exclusivo utilizado por
                        alguns dispositivos eletrônicos para identificar e comunicar
                        uns com os outros na Internet. Quando você consulta nosso
                        site, podemos utilizar o endereço IP do dispositivo usado por
                        você para conectá-lo ao site.
                      </p>
                      <p>
                        Utilizamos estas informações para determinar a localização
                        física geral do dispositivo e para saber em quais áreas
                        geográficas os visitantes estão localizados. Você pode decidir
                        se deseja ativar ou desativar a localização através do seu
                        dispositivo eletrônico.
                      </p>
                      <h4>Estatísticas</h4>
                      <p>
                        O site utiliza o Google Analytics, serviço de análise da rede
                        fornecido pela Google, Inc. (&quot;Google&quot;), para gerar
                        relatórios estatísticos. Estes relatórios nos dizem, por
                        exemplo, quantos usuários consultaram o site, quais páginas
                        foram visitadas e em quais áreas geográficas os usuários do
                        site estão localizados.
                      </p>
                      <p>
                        As informações coletadas por meio das estatísticas podem
                        incluir, por exemplo, seu endereço IP, o site origem por onde
                        você chegou ao nosso site e o tipo de dispositivo que você
                        utilizou. Seu endereço IP está oculto em nossos sistemas e
                        somente será utilizado se necessário para resolver um problema
                        técnico, para administração do site e para obter uma visão das
                        preferências de nossos usuários.
                      </p>
                      <p>
                        As informações de tráfego do site somente são acessíveis a
                        pessoas autorizadas. Não utilizamos nenhuma destas informações
                        para identificar os visitantes e não as compartilhamos com
                        terceiros que não estejam diretas ou indiretamente envolvidos
                        na prestação de serviço.
                      </p>
                      <p>
                        O site utiliza o Google reCAPTCHA, serviço para impedir que
                        softwares que vasculham a Internet criem tráfego artificial ou
                        mesmo sobrecarreguem um serviço <strong>ALTATECH</strong>,
                        tornando o seu acesso mais difícil para usuários reais.
                      </p>
                      <p>
                        As políticas do Google estão disponíveis abaixo, com as quais
                        o Você concorda ao aceitar a presente Política de Privacidade.
                        Quaisquer usos feitos pelo Google ou seus parceiros dos Dados
                        Pessoais coletados por meio dessas ferramentas serão de
                        responsabilidade única e exclusiva do Google:
                      </p>
                      <section>
                        <ul>
                          <li>
                            <a
                              id="google-policies-links"
                              target="_blank"
                              href="https://www.google.com/intl/pt-BR/policies/privacy/partners/"
                            >
                              https://www.google.com/intl/pt-BR/policies/privacy/partners/
                          </a>
                          </li>
                          <li>
                            <a
                              id="google-pivacy-link"
                              target="_blank"
                              href="https://policies.google.com/privacy"
                            >
                              https://policies.google.com/privacy
                          </a>
                          </li>
                          <li>
                            <a
                              id="google-terms-link"
                              target="_blank"
                              href="https://policies.google.com/terms"
                            >
                              https://policies.google.com/terms
                          </a>
                          </li>
                        </ul>
                      </section>
                      <h4>Redes Sociais</h4>
                      <p>
                        Você tem a opção de clicar nos ícones dedicados de redes
                        sociais como YouTube, Facebook e Linkedin que aparecem em
                        nosso site.
                      </p>
                      <p>
                        As redes sociais criam uma atmosfera mais amigável no site e
                        ajudam a promovê-lo por meio do compartilhamento. Os serviços
                        de compartilhamento de vídeo enriquecem o conteúdo de vídeo de
                        nosso site e aumentam sua visibilidade.
                      </p>
                      <p>
                        Quando você clica nos ícones, podemos ter acesso aos Dados
                        Pessoais que Você tornou públicos e acessíveis por meio de
                        seus perfis nas redes sociais em questão. Não criamos nem
                        utilizamos nenhum banco de dados separado destas redes sociais
                        com base nos Dados Pessoais que Você publicou ali e não
                        utilizamos estes meios para tratar nenhum dado relacionado à
                        sua vida privada.
                      </p>
                      <p>
                        Se você não quiser que tenhamos acesso aos seus Dados Pessoais
                        publicados nos espaços públicos de seu perfil ou de suas
                        contas sociais, deverá utilizar os procedimentos fornecidos
                        pelas redes sociais em questão para limitar o acesso a tais
                        dados.
                      </p>
                    </div>
                    <li>FINALIDADES PARA AS QUAIS UTILIZAMOS DADOS PESSOAIS</li>
                    <div id="finalidades-dados-pessoais">
                      <p>
                        Os Dados Pessoais que a <strong>ALTATECH</strong> coleta são
                        utilizados para fornecer os seus serviços, garantir que
                        funcionem conforme o esperado, para aprimorá-los e, também,
                        para ajudar a melhorar a confiabilidade e segurança de todos
                        os nossos serviços.
                      </p>
                      <p>
                        Especificamente, a <strong>ALTATECH</strong> poderá utilizar
                        seus Dados Pessoais para as seguintes finalidades:
                      </p>
                      <p>
                        Atender às suas solicitações de dúvidas/suporte bem como de
                        serviços, troca de senha, reset ou outro conteúdo;
                        <br />
                        Administrar, prestar os serviços e cumprir as obrigações
                        decorrentes dos serviços disponibilizados pela
                        <strong>ALTATECH</strong>;
                        <br />
                        Informar sobre novidades, serviços, funcionalidades,
                        conteúdos, benefícios, promoções, notícias e demais eventos
                        relevantes e de relacionamento com os usuários;
                        <br /> Informar sobre eventos comerciais relevantes e de
                        relacionamento com os representantes legais;
                        <br /> Registrar e realizar a gestão de operações efetuadas
                        pelos usuários através do portal <strong>ALTATECH</strong>;
                        Realizar o controle de Logan e autenticação, bem como para
                        cadastro dos usuários no site ou App;
                        <br /> Criação de plano de ação de melhoria de nossos
                        serviços/produtos;
                        <br /> Para personalizar e aprimorar sua experiência em nosso
                        site;
                        <br /> Para lhe oferecer nossos produtos e serviços e/ou os
                        produtos e serviços de nossos parceiros;
                        <br /> Colaborar e/ou cumprir ordem judicial ou requisição por
                        autoridade administrativa;
                        <br /> qualquer outra finalidade que será informada a você, se
                        aplicável, quando coletamos seus dados.
                      </p>
                      <p>
                        Você está ciente que a <strong>ALTATECH</strong> realiza,
                        quando necessário, o enriquecimento da sua base de dados,
                        adicionando informações oriundas de outras fontes legítimas.
                      </p>
                      <p>
                        Você deve ter conhecimento que, para fins de monitoramento
                        antifraude e prevenção à lavagem de dinheiro, a
                        <strong>ALTATECH</strong> extrai e utiliza Dados Pessoais de
                        fontes públicas, Dados Pessoais fornecidos por terceiros (como
                        instituições financeiras), que podem vir a ser associados aos
                        Dados Pessoais que a <strong>ALTATECH</strong> mantém sobre
                        Você. A <strong>ALTATECH</strong> não vende e/ou comercializa
                        informações que possam identificá-lo.
                      </p>
                    </div>
                    <li>BASE LEGAL PARA O TRATAMENTO DE DADOS PESSOAIS</li>
                    <div id="base-legal">
                      <p>
                        A <strong>ALTATECH</strong> realiza o Tratamento dos Dados
                        Pessoais em correspondência com as Bases Legais estabelecidas
                        pela LGPD e de forma adequada e compatível com a finalidade da
                        sua coleta. Tratamos seus Dados Pessoais como parte do
                        desempenho e gestão de nosso relacionamento contratual com
                        você e/ou com a empresa contratante dos nossos serviços, em
                        nosso legítimo interesse ou de terceiros, desde que os
                        requisitos legais para tanto sejam atendidos, em conformidade
                        com certas obrigações legais ou regulatórias, ou quando for
                        necessário para o exercício regular de direitos em processo
                        judicial, administrativo ou arbitral.
                      </p>
                      <p>
                        Seus Dados Pessoais também podem ser tratados com base em seu
                        prévio consentimento, caso, sob certas circunstâncias, seu
                        consentimento seja solicitado.
                      </p>
                    </div>
                    <li>COMPARTILHAMENTO DE DADOS PESSOAIS</li>
                    <div id="compartilhamento-de-dados-pessoais">
                      <p>
                        A segurança e a confidencialidade de seus Dados Pessoais são
                        de grande importância para nós. É por isso que restringimos o
                        acesso a seus Dados Pessoais somente aos nossos profissionais
                        devidamente autorizados que precisem ter conhecimento sobre
                        tais dados para processar seus pedidos ou fornecer o (s)
                        serviço (s) solicitado (s).
                      </p>
                      <p>
                        Não divulgaremos seus Dados Pessoais a terceiros não
                        autorizados. No entanto, podemos compartilhar seus Dados
                        Pessoais com entidades dentro do grupo
                        <strong>ALTATECH</strong> e com prestadores de serviços
                        autorizados, com os quais trabalhamos (por exemplo: Call
                        Center; agentes de relacionamento; serviços de BackOffice;
                        consultores nas áreas de tecnologia e informática; etc.), a
                        quem podemos recorrer com o propósito de prestar nossos
                        serviços. Nesses casos, garantimos que os seus Dados Pessoais
                        sejam tratados apenas para propósitos legítimos, específicos,
                        explícitos e informados a você, sem possibilidade de
                        Tratamento posterior de forma incompatível com a finalidade
                        informada.
                      </p>
                      <p>
                        Caso empresas terceirizadas realizem o Tratamento de quaisquer
                        dados coletados por meio do nosso site e/ou App, deverão
                        respeitar as condições aqui estipuladas e de acordo com a
                        legislação vigente aplicável, obrigatoriamente.
                      </p>
                      <p>
                        Não autorizamos nossos prestadores de serviços a utilizar e/ou
                        divulgar seus Dados Pessoais, exceto na medida necessária para
                        prestar os serviços em nosso nome ou para cumprir obrigações
                        legais. Nossos prestadores de serviços somente utilizarão seus
                        Dados Pessoais de acordo com as instruções emitidas pela
                        <strong>ALTATECH</strong> e em conformidade com as finalidades
                        mencionadas no presente Política.
                      </p>
                      <p>
                        Além disso, podemos compartilhar Dados Pessoais a seu
                        respeito, quando acreditamos, de boa-fé, que temos a obrigação
                        de:
                      </p>
                      <section>
                        <ol type="I">
                          <li>Cumprir uma lei, regulamento ou ordem judicial;</li>
                          <li>
                            Responder solicitações de informação feitas por órgão
                            regulador, autoridade policial e demais autoridades
                            públicas, inclusive em casos relativos à segurança
                            nacional;
                          </li>
                          <li>
                            Fazer cumprir e/ou proteger propriedade ou direitos da
                            <strong>ALTATECH</strong>, inclusive para investigar
                            fraudes e ajudar a prevenir ameaças de segurança,
                            inclusive da informação, ou outras atividades criminosas
                            ou maliciosas; ou
                          </li>
                          <li>
                            Proteger os interesses legítimos de terceiros, incluindo a
                            segurança pessoal dos funcionários da
                            <strong>ALTATECH</strong>.
                          </li>
                        </ol>
                      </section>
                    </div>
                    <li>REGISTRO DE ATIVIDADES</li>
                    <div id="registro-de-atividades">
                      <p>
                        Para execução das suas atividades, a <strong>ALTATECH</strong>
                        poderá registrar, caso aplicável, os acessos efetuados pelos
                        usuários em nosso site e/ou App, incluindo:
                      </p>
                      <p>
                        Endereço IP e Porta Lógica de Origem dos usuários;
                        <br />
                        Ações efetuadas pelos usuários no site e/ou App; <br /> Telas
                        acessadas pelos usuários; Datas e horários de cada atividade
                        efetuada, bem como o acesso dos usuários no site e/ou App;
                        <br />
                        Dados sobre o dispositivo utilizado pelo usuário, em especial:
                        versão de sistema operacional, geolocalização e outros
                        aplicativos instalados, se necessário;
                        <br /> Session ID dos usuários;
                        <br /> Histórico de operações realizadas;
                        <br /> Comportamentos identificados na interação com o site
                        e/ou App.
                      </p>
                      <p>
                        O processo tem como objetivo coletar e analisar logs de
                        acesso, para controles internos, apoio às auditorias e
                        rastreabilidade. Outras tecnologias poderão ser utilizadas
                        para a obtenção de dados de navegação dos usuários, sempre
                        respeitando as condições do presente Política.
                      </p>
                    </div>
                    <li>ARMAZENAMENTO DE SEUS DADOS PESSOAIS</li>
                    <div id="armazenamento-de-dados">
                      <p>
                        Armazenamos seus Dados Pessoais em nossas bases, em ambiente
                        seguro, garantindo o sigilo e a sua confidencialidade, somente
                        pelo tempo necessário para cumprir as finalidades para as
                        quais foram coletados, observados os eventuais prazos legais e
                        a necessidade de defesa da <strong>ALTATECH</strong> em
                        processos. No entanto, eventualmente, podemos ter que manter
                        os seus Dados Pessoais por um período superior, por motivo de
                        leis ou regulamentos aplicáveis. Findo o prazo e a necessidade
                        legal, os seus Dados Pessoais serão excluídos com uso de
                        métodos de descarte seguro ou utilizados de forma anonimizada
                        para fins estatísticos.
                      </p>
                      <p>
                        Os Dados Pessoais obtidos poderão ser armazenados em servidor
                        próprio da <strong>ALTATECH</strong> ou de terceiro contratado
                        para esse fim, sejam eles alocados no Brasil ou no exterior,
                        podendo, ainda, ser armazenados por meios de tecnologia de
                        computação em nuvem, também conhecidos como cloud computing
                        e/ou outras que surjam futuramente, visando sempre a melhoria
                        e aperfeiçoamento de suas atividades, respeitando os níveis de
                        segurança e boas práticas do mercado.
                      </p>
                    </div>
                    <li>DADOS PESSOAIS SENSÍVEIS</li>
                    <div id="dados-pessoais-sensiveis">
                      <p>
                        &quot;Dados Pessoais Sensíveis&quot; são qualquer informação
                        relativa à origem racial ou étnica, convicção religiosa,
                        opinião política, filiação a sindicato ou a organização de
                        caráter religioso, filosófico ou político, dados de saúde ou
                        dados relativos à orientação sexual, dado genético ou
                        biométrico, quando vinculado a uma pessoa natural.
                      </p>
                      <p>
                        Como regra geral, não coletamos Dados Pessoais Sensíveis por
                        meio de nosso site e/ou App.
                      </p>
                      <p>
                        Caso seja estritamente necessário coletar tais dados para
                        atingir a finalidade para a qual o tratamento é realizado, o
                        faremos de acordo com as exigências legais locais para a
                        proteção de Dados Pessoais e, em particular, com seu prévio
                        consentimento específico e destacado, para finalidades
                        específicas sob as condições descritas nesta Política.
                      </p>
                    </div>
                    <li>TRATAMENTO DE DADOS PESSOAIS DE CRIANÇAS E ADOLESCENTES</li>
                    <div id="tratamento-dados-pessoais">
                      <p>
                        A <strong>ALTATECH</strong> não coleta intencionalmente Dados
                        Pessoais de crianças e adolescentes pelo site e/ou pelo App.
                        Para a utilização do site e/ou App o usuário deverá ter, no
                        mínimo, 14 (quatorze) anos e, se menor de 18 (dezoito) anos,
                        ser assistido pelos pais ou responsáveis legais, conforme
                        previsto nos Termos de Uso Clique aqui para conhecer os Termos
                        e Condições de Uso: devendo esses serem responsáveis por todo
                        e qualquer ato praticado pelos menores quando da utilização
                        dos site e/ou App.
                      </p>
                    </div>
                    <li>TRANSFERÊNCIA INTERNACIONAL DE DADOS PESSOAIS </li>
                    <div id="transferencia-internacional">
                      <p>
                        Como a <strong>ALTATECH</strong> é um grupo internacional,
                        seus Dados Pessoais podem ser transferidos para destinatários
                        localizados fora do Brasil e autorizados a realizar serviços
                        em nosso nome. As leis de proteção de Dados Pessoais variam de
                        acordo com cada país, mas, na hipótese de transferência
                        internacional com destinatários localizados em outros países,
                        adotaremos as medidas necessárias para assegurar a existência
                        de garantias adequadas para a proteção dos seus Dados Pessoais
                        de acordo com a LGPD e em observância às proteções descritas
                        no presente Política.
                      </p>
                      <p>
                        Apesar de existirem disposições legais expressas que autorizem
                        a Transferência Internacional além das que estão aqui
                        indicadas, é mais provável que a <strong>ALTATECH</strong>
                        transfira os seus Dados Pessoais para o exterior em uma das
                        seguintes hipóteses:
                      </p>
                      <section>
                        <ol type="I">
                          <li>
                            Quando for efetuada para países ou organismos
                            internacionais que proporcionem grau de proteção de Dados
                            Pessoais adequado ao previsto na LGPD;
                          </li>
                          <li>
                            O titular dos Dados Pessoais tiver fornecido o seu
                            consentimento específico e em destaque para a
                            transferência; ou
                          </li>
                          <li>
                            Quando houver cláusulas contratuais submetidas à aprovação
                            da Autoridade Nacional de Proteção de Dados (ANPD).
                          </li>
                        </ol>
                      </section>
                    </div>
                    <li>EXERCÍCIO DE SEUS DIREITOS DECORRENTES DA LGPD</li>
                    <div id="exercicio-lgpd">
                      <p>
                        À <strong>ALTATECH</strong> respeita os direitos que você
                        possui na qualidade de titular de Dados Pessoais e
                        possibilitamos seu exercício em conformidade com a legislação
                        aplicável. Abaixo você encontrará uma tabela que resume seus
                        diferentes direitos:
                      </p>
                      <h4>
                        Direito de confirmação da existência de Tratamento e acesso
                      </h4>
                      <p>
                        Você tem o direito de confirmar se nós fazemos o Tratamento de
                        Dados Pessoais que digam respeito a você e de acessar os Dados
                        Pessoais que Você nos forneceu e/ou que mantemos sobre você.
                      </p>
                      <h4>Direito de correção</h4>
                      <p>
                        Você pode solicitar a correção de seus Dados Pessoais que
                        estiverem incompletos, inexatos ou desatualizados, se
                        fornecidos por você diretamente nos aplicativos
                        <strong>ALTATECH</strong> ou no site. Se fornecido pela
                        contratante <strong>ALTATECH</strong> (seu empregador e/ou
                        empresa que o indicou para recebimento dos serviços), o pedido
                        de correção deve ser feito diretamente à contratante dos
                        serviços <strong>ALTATECH</strong>.
                      </p>
                      <h4>Direito de anonimização, bloqueio ou eliminação</h4>
                      <p>
                        Você pode solicitar a anonimização, bloqueio ou eliminação de
                        dados desnecessários, excessivos ou tratados em
                        desconformidade com o disposto na LGPD.
                      </p>
                      <h4>Direito à informação sobre o compartilhamento dos dados</h4>
                      <p>
                        Você pode solicitar informações das entidades públicas e
                        privadas com as quais nós tenhamos compartilhado os seus Dados
                        Pessoais. Essa informação de compartilhamento de dados
                        pessoais consta no item 7 desta Política, sendo empresas
                        inerentes a execução dos serviços prestados pela
                        <strong>ALTATECH</strong>.
                      </p>
                      <h4>Direito de não fornecer consentimento e as negativas</h4>
                      <p>
                        Você pode solicitar informações sobre a possibilidade de não
                        fornecer consentimento e sobre as possíveis consequências da
                        sua negativa.
                      </p>
                      <h4>Direito à revogação do consentimento</h4>
                      <p>
                        Você pode solicitar a revogação do seu consentimento, a
                        qualquer momento, mediante manifestação expressa, sendo
                        ratificados todos os Tratamentos realizados sob amparo do
                        consentimento anteriormente manifestado. A revogação dar-se-á
                        diretamente no site e nos aplicativos.
                      </p>
                      <h4>Direito à portabilidade dos dados</h4>
                      <p>
                        Quando aplicável, você pode solicitar a portabilidade dos seus
                        Dados Pessoais a outro fornecedor de serviço ou produto,
                        mediante requisição expressa, de acordo com a regulamentação
                        da Autoridade Nacional de Proteção de Dados (ANPD), observados
                        os segredos comercial e industrial. A portabilidade dos Dados
                        Pessoais não inclui dados que já tenham sido anonimizados e/ou
                        eliminados pela <strong>ALTATECH</strong>.
                      </p>
                      <h4>Direito de oposição</h4>
                      <p>
                        Você pode solicitar opor-se a Tratamento realizado com
                        fundamento em uma das hipóteses de dispensa de consentimento,
                        em caso de descumprimento ao disposto na LGPD. A efetivação do
                        exercício desse direito considerará a necessidade do uso dos
                        Dados Pessoais para a execução dos serviços da
                        <strong>ALTATECH</strong> em seu benefício e dependerá de
                        avaliação conjunta com a empresa contratante dos serviços
                        <strong>ALTATECH</strong>, que lhe indicou para receber os
                        serviços <strong>ALTATECH</strong>.
                      </p>
                      <h4>
                        Direito de solicitar a revisão de decisões automatizadas
                      </h4>
                      <p>
                        Você pode solicitar a revisão de decisões tomadas unicamente
                        com base em Tratamento automatizado de Dados Pessoais que
                        afetem seus interesses, incluídas as decisões destinadas a
                        definir o seu perfil pessoal, profissional, de consumo e de
                        crédito ou os aspectos de sua personalidade.
                      </p>
                      <h4>
                        Direito de eliminação dos Dados Pessoais tratados com o
                        consentimento
                      </h4>
                      <section>
                        <p>
                          Você pode solicitar a eliminação dos Dados Pessoais tratados
                          com base no seu consentimento, exceto nas seguintes
                          hipóteses previstas pela LGPD:
                        </p>
                        <ol type="I">
                          <li>
                            Cumprimento de obrigação legal ou regulatória pelo
                            Controlador;
                          </li>
                          <li>
                            Estudo por órgão de pesquisa, garantida, sempre que
                            possível, a anonimização dos Dados Pessoais;
                          </li>
                          <li>
                            Transferência a terceiro, desde que respeitados os
                            requisitos de Tratamento de dados dispostos na LGPD; ou
                          </li>
                          <li>
                            Uso exclusivo do controlador, vedado seu acesso por
                            terceiro, e desde que anonimizados os dados.
                          </li>
                        </ol>
                      </section>
                      <p>
                        Para exercer qualquer um dos seus direitos acima mencionados,
                        veja a seção:
                        <strong>
                          <a
                            id="lei-aplicavel-e-juridicao-link-1"
                            href="lei-aplicavel-e-jurisdicao"
                          >
                            20. FALE CONOSCO SOBRE SEUS DIREITOS DECORRENTES DA LGPD
                        </a>
                        </strong>
                      </p>
                      <p>
                        Para garantir a sua privacidade, e como medida de segurança e
                        prevenção à fraude, podemos solicitar a comprovação da sua
                        identidade antes de efetivarmos os seus direitos.
                      </p>
                    </div>
                    <li>SEGURANÇA APLICÁVEL À PROTEÇÃO DE DADOS PESSOAIS</li>
                    <div id="seguranca-dados-pessoais">
                      <p>
                        Implementamos as melhores práticas de mercado de segurança
                        técnicas e administrativas possíveis para proteger os seus
                        Dados Pessoais de acessos não autorizados e de situações
                        acidentais ou ilícitas de destruição, perda, alteração,
                        comunicação ou difusão, conforme determina a legislação
                        vigente.
                      </p>
                      <p>
                        Para esse fim, tomamos todas as precauções necessárias dada a
                        natureza dos Dados Pessoais e os riscos relacionados ao seu
                        Tratamento, a fim de manter a segurança dos dados e, em
                        particular, evitar distorções, danos ou acesso não autorizado
                        de terceiros (proteção física das instalações, procedimentos
                        de autenticação com acesso pessoal e seguro por meio de
                        identificadores e senhas confidenciais, um registro de
                        conexão, criptografia de certos dados, etc.).
                      </p>
                      <p>
                        No entanto, esteja ciente de que nenhuma empresa, incluindo a
                        <strong>ALTATECH</strong>, pode eliminar totalmente os riscos
                        de segurança associados ao Tratamento de Dados Pessoais. Para
                        a sua segurança, é indispensável que você se proteja do acesso
                        não autorizado à sua senha e ao seu computador, pois seu
                        acesso é de uso pessoal e intransferível, sendo de sua única e
                        exclusiva responsabilidade. Havendo tentativa de acesso
                        indevido ou não autorizado, você deverá comunicar a
                        <strong>ALTATECH</strong> o mais rápido possível.
                      </p>
                    </div>
                    <li>
                      BANCO DE DADOS DE GESTÃO DE RELACIONAMENTO COM O CLIENTE
                      (&quot;BANCO DE DADOS DE CRM&quot;)
                    </li>
                    <div id="banco-de-dados">
                      <p>
                        Utilizamos um banco de dados para gerenciar e monitorar nossos
                        relacionamentos com clientes existentes e potenciais. Este
                        banco de dados inclui os Dados Pessoais dos associados de
                        nossos clientes ou outros parceiros com os quais temos um
                        relacionamento comercial ou com os quais desejamos estabelecer
                        tal relacionamento. Esses dados, utilizados apenas para esta
                        finalidade, incluem, em particular: detalhes de contato (nome,
                        RG, CPF, data de nascimento, e-mail, telefone, etc.)
                        informações acessíveis ao público (por exemplo, através do
                        LinkedIn), respostas a e-mails específicos e outras
                        informações coletadas e registradas por nossos associados como
                        parte de suas interações com nossos clientes e parceiros. Caso
                        deseje informações sobre os Dados Pessoais tratados no CRM,
                        veja a seção:
                        <strong>
                          <a
                            id="lei-aplicavel-e-juridicao-link-2"
                            href="lei-aplicavel-e-jurisdicao"
                          >
                            20. FALE CONOSCO SOBRE SEUS DIREITOS DECORRENTES DA LGPD
                        </a>
                        </strong>
                      </p>
                    </div>
                    <li>APLICAÇÕES DE TERCEIROS</li>
                    <div id="aplicacoes-de-terceiros">
                      <p>
                        Eventualmente, fornecemos links para outros sites para fins
                        práticos e informativos. Esses sites são: os sites da
                        <strong>ALTATECH</strong> e alguns deles operam
                        independentemente dos nossos e não estão sob nosso controle.
                        Eles são administrados por terceiros com suas próprias
                        políticas de privacidade e termos de uso, dos quais
                        aconselhamos a sua leitura para que você entenda como eles
                        coletam e utilizam os seus dados.
                      </p>
                      <p>
                        A <strong>ALTATECH</strong> não se responsabiliza pelas
                        políticas de privacidade, segurança e conteúdo de tais sites
                        ou pelos produtos e serviços que ali possam ser oferecidos,
                        não podendo ser responsabilizada por eventuais perdas e danos
                        sofridos em razão da utilização de referidos links ou frames.
                      </p>
                    </div>
                    <li>ACESSOS E ATUALIZAÇÕES DA NOSSA POLÍTICA DE PRIVACIDADE</li>
                    <div id="acessos-atualizacoes-politica-de-privacidade">
                      <p>
                        Ao acessar e utilizar o site e/ou App da
                        <strong>ALTATECH</strong>, você concorda expressamente com
                        esta Política. A aceitação desta Política de Privacidade é
                        absolutamente indispensável à utilização do site e App.
                      </p>
                      <p>
                        Você deverá ler, certificar-se de haver entendido e aceitar
                        todas as condições estabelecidas nesta Política, assim como
                        nos demais documentos a ela incorporados por referência, antes
                        de realizar o seu cadastro como usuário do site e do App.
                      </p>
                      <p>
                        Ao aceitar os termos dessa Política, você concorda
                        expressamente em fornecer somente Dados Pessoais verdadeiros,
                        atuais e precisos. Você será o único responsável pelas
                        informações falsas, desatualizadas ou imprecisas que fornece
                        diretamente para a <strong>ALTATECH</strong>.
                      </p>
                      <p>
                        A <strong>ALTATECH</strong> poderá recusar qualquer
                        solicitação de cadastro e de suspender um cadastro previamente
                        aceito, que esteja em desacordo com a presente Política.
                      </p>
                      <p>
                        Como estamos sempre buscando melhorar nossos Serviços, a
                        <strong>ALTATECH</strong> reserva o direito de, a qualquer
                        momento e sem prévia notificação alterar, complementar ou
                        atualizar, a qualquer momento, em particular para cumprir
                        quaisquer desdobramentos legais, regulamentares,
                        jurisprudenciais ou técnicos que possam surgir. Entretanto,
                        seus Dados Pessoais serão sempre tratados de acordo com a
                        política em vigor no momento da coleta de dados, salvo se uma
                        disposição legal obrigatória determinar o contrário e deva ser
                        aplicada retroativamente.
                      </p>
                      <p>
                        Caso ocorra qualquer alteração na presente Política,
                        publicaremos a nova versão no site e/ou App, a qual indicará a
                        data em que se deu a revisão, para que você possa estar sempre
                        ciente do conteúdo atualizado.
                      </p>
                      <p>
                        Todas as modificações entrarão em vigor imediatamente após a
                        publicação da Política de Privacidade revisada, a menos que
                        especificado de outra forma. Assim, recomendamos que você
                        consulte periodicamente esta Política, a fim de cientificar
                        sobre as respectivas alterações.
                      </p>
                      <p>
                        Ao utilizar-se dos sites e/ou do App da
                        <strong>ALTATECH</strong> após tais mudanças, você está
                        consentindo novamente com os termos da Política de Privacidade
                        revisada.
                      </p>
                      <p>
                        Caso você não concorde com as mudanças realizadas e os novos
                        termos da nossa Política, interrompa imediatamente o uso do
                        site e/ou App. A continuação do uso do nosso site e/ou App
                        após as alterações desta Política indicará que você aceita e
                        concorda com as mudanças realizadas.
                      </p>
                      <p>
                        A data da última atualização desta Política é indicada no
                        início e reflete a data efetiva dessa Política. As versões
                        anteriores desta Política poderão ser solicitadas pelo e-mail
                        indicado nesta política.
                      </p>
                    </div>
                    <li>TÉRMINO DO USO DO SITE /APP</li>
                    <div id="termino-do-uso-do-site">
                      <p>
                        Caso, a qualquer momento, você queira deixar de utilizar o
                        nosso App, você poderá desinstalá-lo do (s) seu (s)
                        dispositivo (s).
                      </p>
                      <p>
                        Ressaltamos que, em certas circunstâncias, geralmente
                        constante de leis e regulamentos, somos obrigados a manter os
                        seus Dados Pessoais independente do seu pedido de exclusão e
                        por certo período. Findo o prazo e a necessidade legal, os
                        seus Dados Pessoais serão excluídos com o uso de métodos de
                        descarte seguro ou utilizados de forma anonimizada para fins
                        estatísticos.
                      </p>
                    </div>
                    <li>LEI APLICÁVEL E JURISDIÇÃO</li>
                    <div id="lei-aplicavel-e-jurisdicao">
                      <p>
                        A presente Política de Privacidade será regida, interpretada e
                        executada segundo a legislação brasileira, no idioma
                        português, sendo eleito o Foro da Comarca de LONDRINA-PR para
                        dirimir qualquer litígio ou controvérsia envolvendo o presente
                        documento, salvo ressalva específica de competência pessoal,
                        territorial ou funcional pela legislação aplicável. Fora do
                        Estado, a JUSPRO – Justiça Sem Processo Mediação e Conciliação
                        S/S Ltda. como Câmara de Mediação apta a dirimir as questões
                        envolvendo os Usuários titulares dos dados, nos termos do
                        artigo 52, §7º da Lei Geral de Proteção de Dados.
                      </p>
                    </div>
                    <li>FALE CONOSCO SOBRE SEUS DIREITOS DECORRENTES DA LGPD</li>
                    <div id="fale-conosco-direitos-lgpd">
                      <p>
                        A <strong>ALTATECH GROUP</strong> nomeou o Sr. Damiao
                        Oliveira, Profissional Certificado em Privacidade e Proteção
                        de Proteção de Dados, como o CDPO - Chief Data Privacy Officer
                        (Encarregado de Dados) de todo este novo processo e estará à
                        frente das novas práticas que passarão a ser adotadas pelas
                        empresas do GRUPO, que serão implementadas a partir de
                        setembro de 2020, sempre objetivando a manutenção da SEGURANÇA
                        DOS DADOS adotados pela companhia. Linha direta pelo telefone
                        (47) 988606781.
                      </p>
                      <p>
                        Para o exercício de qualquer um dos seus direitos mencionados
                        na seção 12 da presente Política, por exemplo, direito de
                        acesso ou correção dos seus Dados Pessoais, envie a sua
                        solicitação para o seguinte endereço: com sede na Rua
                        Francisco Margarida, 68 Salas 101 e 102, Itoupava Norte,
                        Blumenau, SC ou por e-mail.
                      </p>
                      <p>
                        Se você tiver alguma dúvida sobre esta Política de Privacidade
                        ou sobre nossas práticas em relação à privacidade, entre em
                        contato com o nosso Encarregado de Proteção de Dados do Grupo
                        através do seguinte endereço de e-mail: lgpd@altatech.com.br.
                        O assunto: “LGPD - DPO ALTATECH”, se por carta, a informação
                        deverá ser encaminhada com comprovante de recebimento (AR).Se
                        você tiver alguma dúvida sobre esta Política de Privacidade ou
                        sobre nossas práticas em relação à privacidade, entre em
                        contato com o nosso Encarregado de Proteção de Dados do Grupo
                        através do seguinte endereço de e-mail: lgpd@altatech.com.br.
                      </p>
                    </div>
                  </ol>
                </div>
              </div>
            </a-modal>
          </div>
        </div>
      </a-form>
  </div>
</template>

<script>
import { ref, reactive, toRaw } from 'vue'
import { api } from '@/api'
import store from '@/store'
import { mapActions } from 'vuex'
import { sourceToken } from '@/utils/request'
import { SERVER_MANAGER } from '@/store/mutation-types'
import TranslationMenu from '@/components/header/TranslationMenu'

export default {
  components: {
    TranslationMenu
  },
  data () {
    return {
      idps: [],
      customActiveKey: 'cs',
      customActiveKeyOauth: false,
      loginBtn: false,
      email: '',
      secretcode: '',
      oauthexclude: '',
      socialLogin: false,
      googleprovider: false,
      githubprovider: false,
      googleredirecturi: '',
      githubredirecturi: '',
      googleclientid: '',
      githubclientid: '',
      loginType: 0,
      state: {
        time: 60,
        loginBtn: false,
        loginType: 0
      },
      server: '',
      forgotPasswordEnabled: false,
      modal: {
        open: false,
        policyPrivacyOpen: false,
        confirmLoading: false
      }
    }
  },
  created () {
    if (this.$config.multipleServer) {
      this.server = this.$localStorage.get(SERVER_MANAGER) || this.$config.servers[0]
    }
    this.initForm()
    if (store.getters.logoutFlag) {
      if (store.getters.readyForShutdownPollingJob !== '' || store.getters.readyForShutdownPollingJob !== undefined) {
        clearInterval(store.getters.readyForShutdownPollingJob)
      }
      sourceToken.init()
      this.fetchData()
    } else {
      this.fetchData()
    }
  },
  methods: {
    ...mapActions(['Login', 'Logout', 'OauthLogin']),
    initForm () {
      this.formRef = ref()
      this.form = reactive({
        server: (this.server.apiHost || '') + this.server.apiBase,
        username: this.$route.query?.username || '',
        domain: this.$route.query?.domain || ''
      })
      this.rules = reactive({})
      this.setRules()
    },
    showModal (e) {
      this.modal.open = true
    },
    showModalPrivacy (e) {
      this.modal.policyPrivacyOpen = true
    },
    handleOk (e) {
      this.modal.confirmLoading = true
    },
    handleCancel () {
      this.modal.open = false
      this.modal.policyPrivacyOpen = false
      this.modal.confirmLoading = false
    },
    setRules () {
      if (this.customActiveKey === 'cs' && this.customActiveKeyOauth === false) {
        this.rules.username = [
          {
            required: true,
            message: this.$t('message.error.username'),
            trigger: 'change'
          },
          {
            validator: this.handleUsernameOrEmail,
            trigger: 'change'
          }
        ]
        this.rules.password = [
          {
            required: true,
            message: this.$t('message.error.password'),
            trigger: 'change'
          }
        ]
      } else {
        this.rules.username = []
        this.rules.password = []
      }
    },
    fetchData () {
      api('listIdps').then(response => {
        if (response) {
          this.idps = response.listidpsresponse.idp || []
          this.idps.sort(function (a, b) {
            if (a.orgName < b.orgName) { return -1 }
            if (a.orgName > b.orgName) { return 1 }
            return 0
          })
          this.form.idp = this.idps[0].id || ''
        }
      })
      api('listOauthProvider', {}).then(response => {
        if (response) {
          const oauthproviders = response.listoauthproviderresponse.oauthprovider || []
          oauthproviders.forEach(item => {
            if (item.provider === 'google') {
              this.googleprovider = item.enabled
              this.googleclientid = item.clientid
              this.googleredirecturi = item.redirecturi
            }
            if (item.provider === 'github') {
              this.githubprovider = item.enabled
              this.githubclientid = item.clientid
              this.githubredirecturi = item.redirecturi
            }
          })
          this.socialLogin = this.googleprovider || this.githubprovider
        }
      })
      api('forgotPassword', {}).then(response => {
        this.forgotPasswordEnabled = response.forgotpasswordresponse.enabled
      }).catch((err) => {
        if (err?.response?.data === null) {
          this.forgotPasswordEnabled = true
        } else {
          this.forgotPasswordEnabled = false
        }
      })
    },
    // handler
    async handleUsernameOrEmail (rule, value) {
      const { state } = this
      const regex = /^([a-zA-Z0-9_-])+@([a-zA-Z0-9_-])+((\.[a-zA-Z0-9_-]{2,3}){1,2})$/
      if (regex.test(value)) {
        state.loginType = 0
      } else {
        state.loginType = 1
      }
      return Promise.resolve()
    },
    handleTabClick (key) {
      this.customActiveKey = key
      this.setRules()
    },
    handleGithubProviderAndDomain () {
      this.handleDomain()
      this.$store.commit('SET_OAUTH_PROVIDER_USED_TO_LOGIN', 'github')
    },
    handleGoogleProviderAndDomain () {
      this.handleDomain()
      this.$store.commit('SET_OAUTH_PROVIDER_USED_TO_LOGIN', 'google')
    },
    handleDomain () {
      const values = toRaw(this.form)
      if (!values.domain) {
        this.$store.commit('SET_DOMAIN_USED_TO_LOGIN', '/')
      } else {
        this.$store.commit('SET_DOMAIN_USED_TO_LOGIN', values.domain)
      }
    },
    getGitHubUrl (from) {
      const rootURl = 'https://github.com/login/oauth/authorize'
      const options = {
        client_id: this.githubclientid,
        scope: 'user:email',
        state: 'cloudstack'
      }

      const qs = new URLSearchParams(options)

      return `${rootURl}?${qs.toString()}`
    },
    getGoogleUrl (from) {
      const rootUrl = 'https://accounts.google.com/o/oauth2/v2/auth'
      const options = {
        redirect_uri: this.googleredirecturi,
        client_id: this.googleclientid,
        access_type: 'offline',
        response_type: 'code',
        prompt: 'consent',
        scope: [
          'https://www.googleapis.com/auth/userinfo.profile',
          'https://www.googleapis.com/auth/userinfo.email'
        ].join(' '),
        state: from
      }

      const qs = new URLSearchParams(options)

      return `${rootUrl}?${qs.toString()}`
    },
    handleSubmit (e) {
      e.preventDefault()
      if (this.state.loginBtn) return
      this.formRef.value.validate().then(() => {
        this.state.loginBtn = true

        const values = toRaw(this.form)
        if (this.$config.multipleServer) {
          this.axios.defaults.baseURL = (this.server.apiHost || '') + this.server.apiBase
          store.dispatch('SetServer', this.server)
        }
        if (this.customActiveKey === 'cs') {
          const loginParams = { ...values }
          delete loginParams.username
          loginParams[!this.state.loginType ? 'email' : 'username'] = values.username
          loginParams.password = values.password
          loginParams.domain = values.domain
          if (!loginParams.domain) {
            loginParams.domain = '/'
          }
          this.Login(loginParams)
            .then((res) => this.loginSuccess(res))
            .catch(err => {
              this.requestFailed(err)
              this.state.loginBtn = false
            })
        } else if (this.customActiveKey === 'saml') {
          this.state.loginBtn = false
          var samlUrl = this.$config.apiBase + '?command=samlSso'
          if (values.idp) {
            samlUrl += ('&idpid=' + values.idp)
          }
          window.location.href = samlUrl
        }
      }).catch(error => {
        this.formRef.value.scrollToField(error.errorFields[0].name)
      })
    },
    handleSubmitOauth (provider) {
      this.customActiveKeyOauth = true
      this.setRules()
      this.formRef.value.validate().then(() => {
        const values = toRaw(this.form)
        const loginParams = { ...values }
        delete loginParams.username
        loginParams.email = this.email
        loginParams.provider = provider
        loginParams.secretcode = this.secretcode
        loginParams.domain = values.domain
        if (!loginParams.domain) {
          loginParams.domain = '/'
        }
        this.OauthLogin(loginParams)
          .then((res) => this.loginSuccess(res))
          .catch(err => {
            this.requestFailed(err)
            this.state.loginBtn = false
          })
      })
    },
    loginSuccess (res) {
      this.$notification.destroy()
      this.$store.commit('SET_COUNT_NOTIFY', 0)
      if (store.getters.twoFaEnabled === true && store.getters.twoFaProvider !== '' && store.getters.twoFaProvider !== undefined) {
        this.$router.push({ path: '/verify2FA' }).catch(() => {})
      } else if (store.getters.twoFaEnabled === true && (store.getters.twoFaProvider === '' || store.getters.twoFaProvider === undefined)) {
        this.$router.push({ path: '/setup2FA' }).catch(() => {})
      } else {
        this.$store.commit('SET_LOGIN_FLAG', true)
        this.$router.push({ path: '/dashboard' }).catch(() => {})
      }
    },
    requestFailed (err) {
      if (err && err.response && err.response.data && err.response.data.loginresponse) {
        const error = err.response.data.loginresponse.errorcode + ': ' + err.response.data.loginresponse.errortext
        this.$message.error(`${this.$t('label.error')} ${error}`)
      } else if (err && err.response && err.response.data && err.response.data.oauthloginresponse) {
        const error = err.response.data.oauthloginresponse.errorcode + ': ' + err.response.data.oauthloginresponse.errortext
        this.$message.error(`${this.$t('label.error')} ${error}`)
      } else {
        this.$message.error(this.$t('message.login.failed'))
      }
    },
    onChangeServer (server) {
      const servers = this.$config.servers || []
      const serverFilter = servers.filter(ser => (ser.apiHost || '') + ser.apiBase === server)
      this.server = serverFilter[0] || {}
    }
  }
}
</script>

<style lang="less" scoped>

.user-layout-login {

 display: grid;
 align-items: center;
 justify-items: center;
  // .mobile & {
  //   max-width: 368px;
  //   width: 98%;
  // }
  .container-form {
    width: 100%;
    max-width: 500px;
    margin-left: 30px;
    margin-right: 30px;
    display: grid;
    justify-items: center;
  }
  .container-image {
    position: relative;
    height: 80px;
    background: #fff;
    width: 80px;
    border: 1px solid #E7ECFF;
    border-radius: 50%;
    display: grid;
    justify-items: center;
    align-items: center;
    top: 40px;
  }
  form {
    border: 1px solid #E7ECFF;
    padding: 60px;
    border-radius: 0px 20px 20px 0px;
    width: 100%;
    background-color: #fff;
    display: block;

    @media (max-width:980px) {
      border-radius: 0px 0px 20px 20px;
    }

    @media (max-width: 480px) {
      border-radius: 20px 20px 20px 20px;
    }
  }
  label {
    font-size: 14px;
  }

  button.login-button {
    margin-top: 8px;
    padding: 0 15px;
    font-size: 16px;
    height: 40px;
    width: 100%;
  }

  .user-login-other {
    text-align: left;
    margin-top: 24px;
    line-height: 22px;

    .item-icon {
      font-size: 24px;
      color: rgba(0, 0, 0, 0.2);
      margin-left: 16px;
      vertical-align: middle;
      cursor: pointer;
      transition: color 0.3s;

      &:hover {
        color: #1890ff;
      }
    }

    .register {
      float: right;
    }

    .g-btn-wrapper {
      background-color: rgb(221, 75, 57);
      height: 40px;
      width: 80px;
    }
  }
    .center {
    //  display: flex;
    display: none;
     flex-direction: column;
     justify-content: center;
     align-items: center;
     height: 100px;
    }

    .content {
      margin: 10px auto;
      width: 300px;
    }

    .or {
      text-align: center;
      font-size: 16px;
      background:
        linear-gradient(#CCC 0 0) left,
        linear-gradient(#CCC 0 0) right;
      background-size: 40% 1px;
      background-repeat: no-repeat;
    }
}

.textsContainer {
 overflow-x: hidden;
 overflow-y: auto;
 max-height: 360px;
 padding-right: 10px;
}

.downloadBlock {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
